// Time slider (bottom centre): scrub 2000 → as-of, play/pause, speed, and an event-density
// sparkline (world.eventCounts(): bullets per month, captioned with its publisher and a source
// chip; no bars are drawn until the counts carry a source).
//
//   [▶] [2×]  ▁▂▃▂▅▃▂▄▆▅▃▂▃▅▇▅▃  ← events per month  [📖]
//             |    |    |    ◆    |     ← thumb (drag = actions.setDate, throttled)
//            2000 2004 2008 2012 2016 … 2026

import { useSignal } from '@preact/signals';
import { useRef } from 'preact/hooks';
import { EPOCH_START, addDays, daysBetween, yearOf } from '../core/dates.ts';
import type { DateStr, SourceId } from '../core/schema.ts';
import type { WorldService } from '../services/api.ts';
import { useServices } from './context.ts';
import { fmtDate, fmtInt } from './format.ts';
import { Icon } from './icons.tsx';
import { Corners, SourceChip, useTip } from './parts.tsx';

const THROTTLE_MS = 120;
const SPEEDS = [1, 2, 4, 8];
const SPARK_H = 30;
const MONTHS_PER_YEAR = 12;
const WIDE_TRACK_PX = 900;

// ─── Event density, cached per world service ─────────────────────────────────────────────

interface Density {
  /** Bullets per month, index = (year - 2000) * 12 + month. -1 = no count. */
  counts: Int32Array;
  total: number;
  sources: SourceId[];
}

const densityCache = new WeakMap<WorldService, Density>();

function densityOf(world: WorldService, asOf: DateStr): Density | null {
  const hit = densityCache.get(world);
  if (hit) {
    return hit;
  }

  let raw: ReturnType<WorldService['eventCounts']> | null = null;
  try {
    raw = world.eventCounts();
  } catch {
    raw = null;
  }
  // Unsourced counts are never drawn (truth rule 1).
  if (!raw || raw.sources.length === 0 || raw.months.length === 0) {
    return null;
  }

  const first = yearOf(EPOCH_START);
  const counts = new Int32Array((yearOf(asOf) - first + 1) * MONTHS_PER_YEAR).fill(-1);
  let total = 0;
  for (const [month, n] of raw.months) {
    const i = (Number(month.slice(0, 4)) - first) * MONTHS_PER_YEAR + Number(month.slice(5, 7)) - 1;
    if (i >= 0 && i < counts.length) {
      counts[i] = n;
      total += n;
    }
  }
  const density = { counts, total, sources: raw.sources };
  densityCache.set(world, density);
  return density;
}

// ─── Component ───────────────────────────────────────────────────────────────────────────

export function TimeSlider() {
  const { state, actions, world } = useServices();
  const asOf = state.asOf.value;
  const date = state.date.value;
  const drag = useSignal<DateStr | null>(null);
  const track = useRef<HTMLDivElement>(null);
  const lastSent = useRef(0);
  const density = densityOf(world, asOf);
  const publisher = density ? world.source(density.sources[0])?.publisher ?? 'unresolved source' : null;

  const total = daysBetween(EPOCH_START, asOf);
  const shown = drag.value ?? date;
  const frac = total > 0 ? daysBetween(EPOCH_START, shown) / total : 1;

  const dateAt = (clientX: number): DateStr => {
    const r = track.current?.getBoundingClientRect();
    if (!r || r.width === 0) {
      return date;
    }
    const t = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    return addDays(EPOCH_START, Math.round(t * total));
  };

  const send = (d: DateStr, when: 'throttled' | 'now') => {
    const now = performance.now();
    if (when === 'throttled' && now - lastSent.current < THROTTLE_MS) {
      return;
    }
    lastSent.current = now;
    actions.setDate(d);
  };

  const onDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (state.playing.value) {
      actions.setPlaying(false);
    }
    drag.value = dateAt(e.clientX);
    send(drag.value, 'now');
  };
  const onMove = (e: PointerEvent) => {
    if (drag.value === null) {
      return;
    }
    drag.value = dateAt(e.clientX);
    send(drag.value, 'throttled');
  };
  const onUp = () => {
    if (drag.value === null) {
      return;
    }
    send(drag.value, 'now');
    drag.value = null;
  };

  // Arrow keys step a week; PageUp/PageDown a year.
  const onKey = (e: KeyboardEvent) => {
    const step: Record<string, number> = { ArrowRight: 7, ArrowLeft: -7, PageUp: 365, PageDown: -365 };
    const days = step[e.key];
    if (days === undefined) {
      return;
    }
    e.preventDefault();
    actions.setDate(addDays(date, days));
  };

  return (
    <div class="wc-time wc-panel">
      <Corners />
      <div class="wc-time-controls">
        <button
          type="button"
          class="wc-ibtn wc-time-play"
          aria-label={state.playing.value ? 'Pause' : 'Play'}
          aria-pressed={state.playing.value}
          disabled={date >= asOf && !state.playing.value}
          onClick={() => actions.setPlaying(!state.playing.value)}
        >
          <Icon name={state.playing.value ? 'pause' : 'play'} size={18} />
        </button>
        <SpeedButton />
      </div>

      <div class="wc-time-main">
        <div class="wc-time-head">
          <span class="wc-time-label">Timeline</span>
          <span class="wc-time-date">{fmtDate(shown)}</span>
          {density ? (
            <span class="wc-time-caption">
              <span>Events per month · {publisher}</span>
              <SourceChip ids={density.sources} />
            </span>
          ) : (
            <span class="wc-time-caption">No sourced event counts</span>
          )}
        </div>
        <div
          ref={track}
          class="wc-time-track"
          role="slider"
          tabIndex={0}
          aria-label="Date"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={Math.round(frac * total)}
          aria-valuetext={fmtDate(shown)}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKey}
        >
          <Sparkline density={density} frac={frac} />
          <div class="wc-time-rail">
            <div class="wc-time-fill" style={{ width: `${frac * 100}%` }} />
          </div>
          <YearTicks asOf={asOf} total={total} />
          <div class="wc-time-thumb" style={{ left: `${frac * 100}%` }}>
            <span class="wc-time-gem" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeedButton() {
  const { state, actions } = useServices();
  const speed = state.speed.value;
  const handlers = useTip(() => <div class="wc-tip-title">Playback: {speed} turn{speed > 1 ? 's' : ''} per second</div>, 'above');
  const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
  return (
    <button type="button" class="wc-time-speed" aria-label={`Speed ${speed}×`} onClick={() => actions.setSpeed(next)} {...handlers}>
      <Icon name="speed" size={13} />
      <span class="wc-num">{speed}×</span>
    </button>
  );
}

function YearTicks({ asOf, total }: { asOf: DateStr; total: number }) {
  const first = yearOf(EPOCH_START);
  const last = yearOf(asOf);
  const wide = (typeof window !== 'undefined' ? window.innerWidth : 0) >= WIDE_TRACK_PX * 1.6;
  const labelEvery = wide ? 2 : 4;

  const ticks = [];
  for (let y = first; y <= last; y++) {
    const t = daysBetween(EPOCH_START, `${y}-01-01`) / total;
    const major = (y - first) % labelEvery === 0;
    ticks.push(
      <span key={y} class={major ? 'wc-tick wc-tick--major' : 'wc-tick'} style={{ left: `${t * 100}%` }}>
        {major ? <span class="wc-tick-label">{y}</span> : null}
      </span>,
    );
  }
  return <div class="wc-time-ticks">{ticks}</div>;
}

// Monthly bars as one SVG path; the part before the thumb is brighter.
function Sparkline({ density, frac }: { density: Density | null; frac: number }) {
  if (!density) {
    return <div class="wc-spark" />;
  }
  const n = density.counts.length;
  let max = 1;
  for (const c of density.counts) {
    max = Math.max(max, c);
  }

  let d = '';
  for (let i = 0; i < n; i++) {
    const c = density.counts[i];
    if (c <= 0) {
      continue;
    }
    const h = (c / max) * SPARK_H;
    d += `M${i} ${SPARK_H}v${-h.toFixed(2)}h0.72v${h.toFixed(2)}z`;
  }

  return (
    <svg class="wc-spark" viewBox={`0 0 ${n} ${SPARK_H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <clipPath id="wc-spark-past">
          <rect x="0" y="0" width={frac * n} height={SPARK_H} />
        </clipPath>
        <linearGradient id="wc-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#f6dea0" />
          <stop offset="1" stop-color="#8a6a32" />
        </linearGradient>
      </defs>
      <path d={d} fill="#6f8796" opacity="0.4" />
      <path d={d} fill="url(#wc-spark-fill)" opacity="0.85" clip-path="url(#wc-spark-past)" />
      <title>{`Events per month, ${fmtInt(density.total)} in all`}</title>
    </svg>
  );
}
